import React, { useState, useEffect } from 'react'
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  Spinner,
  Breadcrumb,
  Badge,
  Button,
} from 'react-bootstrap'
import { useParams, useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import useUserDetail from 'hooks/use-user-detail'
import useGetAppleIdByUserId from 'hooks/use-get-apple-id-by-user-id'
import usePermissionEvents, {
  PermissionEventFilters,
} from 'hooks/use-permission-events'
import usePermissionEventsSummary from 'hooks/use-permission-events-summary'
import BehavioralProfileOverview from 'components/behavioral-profile-overview'
import PermissionEventsTable from 'components/permission-events-table'
import PermissionEventsFilters from 'components/permission-events-filters'
import IndividualBehavioralProfileModal from 'components/individual-behavioral-profile-modal'
import useIndividualBehavioralProfile from 'hooks/use-individual-behavioral-profile'
import { DEFAULT_PERMISSION_EVENTS_PAGE_SIZE } from 'constants/filters'
import { RootState } from 'index'
import { OverallBehavioralProfile } from 'interfaces/behavioral-profile'

const UserPanopticonProfilePage: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>()
  const history = useHistory()
  const currentUser = useSelector((state: RootState) => state.user.currentUser)

  const [profile, setProfile] = useState<OverallBehavioralProfile | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Permission Events state
  const [filters, setFilters] = useState<PermissionEventFilters>({
    limit: DEFAULT_PERMISSION_EVENTS_PAGE_SIZE,
  })
  const [allEvents, setAllEvents] = useState<any[]>([])
  const [currentCursor, setCurrentCursor] = useState<string | undefined>()

  // Individual Behavioral Profile Modal state
  const [showIndividualModal, setShowIndividualModal] = useState(false)
  const [selectedSystemUser, setSelectedSystemUser] = useState<string>('')
  const [selectedRecipient, setSelectedRecipient] = useState<string>('')

  // Get user details to extract phone number
  const {
    data: userDetail,
    isLoading: isUserLoading,
    error: userError,
  } = useUserDetail(uuid)

  // Get Apple ID details to extract phone number
  const { data: appleId, isLoading: isAppleIdLoading } = useGetAppleIdByUserId(
    userDetail?.user?.id || ''
  )

  // Extract phone number from user detail
  const phoneNumber =
    appleId?.phone_number || userDetail?.user?.worknumber?.number

  // Permission events hooks
  const {
    data: eventsData,
    isLoading: isEventsLoading,
    error: eventsError,
  } = usePermissionEvents(
    phoneNumber || '',
    { ...filters, cursor: currentCursor },
    !!phoneNumber && !isAppleIdLoading
  )

  // Summary for last 7 days
  const last7Days = new Date()
  last7Days.setDate(last7Days.getDate() - 7)
  const today = new Date()

  const { data: summaryData, isLoading: isSummaryLoading } =
    usePermissionEventsSummary(
      phoneNumber || '',
      last7Days.toISOString().split('T')[0],
      today.toISOString().split('T')[0],
      undefined,
      !!phoneNumber && !isAppleIdLoading
    )

  // Individual behavioral profile hook
  const {
    data: individualProfileData,
    isLoading: isIndividualProfileLoading,
    error: individualProfileError,
  } = useIndividualBehavioralProfile(
    selectedSystemUser,
    selectedRecipient,
    showIndividualModal && !!selectedSystemUser && !!selectedRecipient
  )

  // Fetch behavioral profile data
  useEffect(() => {
    const fetchProfile = async () => {
      // Wait for Apple ID loading to complete and ensure we have a phone number
      if (isAppleIdLoading || !phoneNumber) return

      setLoading(true)
      setError(null)

      try {
        const baseUrl = process.env.REACT_APP_PANOPTICON_CAN_SEND_BASE
        if (!baseUrl) {
          // Silently skip if API base URL not configured
          setProfile(null)
          setLoading(false)
          return
        }

        const response = await fetch(
          `${baseUrl}/permission-events/overall-profiles/${encodeURIComponent(
            phoneNumber
          )}`
        )

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const data = await response.json()

        if (data.success && data.profile) {
          setProfile(data.profile)
        } else {
          setProfile(null)
        }
      } catch (err) {
        console.error('Error fetching behavioral profile:', err)
        setError(err instanceof Error ? err : new Error('Unknown error'))
      } finally {
        setLoading(false)
      }
    }

    fetchProfile()
  }, [phoneNumber, isAppleIdLoading])

  // Handle permission events filter changes
  const handleFilterChange = (newFilters: PermissionEventFilters) => {
    setFilters(newFilters)
    setAllEvents([]) // Reset events when filters change
    setCurrentCursor(undefined) // Reset cursor
  }

  // Handle load more
  const handleLoadMore = () => {
    if (eventsData?.pagination.nextCursor) {
      setCurrentCursor(eventsData.pagination.nextCursor)
    }
  }

  // Handle permission event click
  const handleEventClick = (systemUser: string, recipient: string) => {
    setSelectedSystemUser(systemUser)
    setSelectedRecipient(recipient)
    setShowIndividualModal(true)
  }

  // Handle modal close
  const handleModalClose = () => {
    setShowIndividualModal(false)
    setSelectedSystemUser('')
    setSelectedRecipient('')
  }

  // Update events when new data comes in
  useEffect(() => {
    if (eventsData) {
      if (currentCursor) {
        // Append to existing events
        setAllEvents((prev) => [...prev, ...(eventsData.data || [])])
      } else {
        // Replace with new events (filter change)
        setAllEvents(eventsData.data || [])
      }
    }
  }, [eventsData, currentCursor])

  // Redirect if user doesn't have permission to view this page
  useEffect(() => {
    if (userDetail && currentUser) {
      // Add any permission checks here if needed
    }
  }, [userDetail, currentUser])

  if (isUserLoading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    )
  }

  if (userError) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Error Loading User</Alert.Heading>
          <p>Unable to load user details. Please try again.</p>
        </Alert>
      </Container>
    )
  }

  if (!userDetail?.user) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">
          <Alert.Heading>User Not Found</Alert.Heading>
          <p>The requested user could not be found.</p>
        </Alert>
      </Container>
    )
  }

  const userName = `${userDetail.user.first_name || ''} ${
    userDetail.user.last_name || ''
  }`.trim()

  // Get status indicators for header
  const getStatusInfo = () => {
    if (!profile)
      return { variant: 'secondary', text: 'Loading...', icon: '⏳' }

    if (profile.overallRiskScore >= 80)
      return { variant: 'danger', text: 'High Risk', icon: '🚨' }
    if (profile.overallRiskScore >= 60)
      return { variant: 'warning', text: 'Medium Risk', icon: '⚠️' }
    if (profile.qualityTier === 'MASS_MARKETING')
      return { variant: 'danger', text: 'Spam Pattern', icon: '🚫' }
    if (profile.qualityTier === 'EXCELLENT')
      return { variant: 'success', text: 'Excellent Quality', icon: '✅' }
    return { variant: 'success', text: 'Good Standing', icon: '✅' }
  }

  const statusInfo = getStatusInfo()

  return (
    <Container
      style={{
        backgroundColor: '#1a1a1a',
        minHeight: '100vh',
        maxWidth: '1400px',
      }}
      className="px-4 py-3"
    >
      {/* Breadcrumb */}
      <Breadcrumb className="mb-3">
        <Breadcrumb.Item onClick={() => history.push('/')}>
          Home
        </Breadcrumb.Item>
        <Breadcrumb.Item onClick={() => history.push('/users')}>
          Users
        </Breadcrumb.Item>
        <Breadcrumb.Item onClick={() => history.push(`/user/${uuid}`)}>
          {userName || 'User'}
        </Breadcrumb.Item>
        <Breadcrumb.Item active>Panopticon Profile</Breadcrumb.Item>
      </Breadcrumb>

      {/* Page Title */}
      <div className="mb-4">
        <h1 className="text-white mb-0">
          {userName || 'User'} - Panopticon Profile
        </h1>
      </div>

      {!phoneNumber ? (
        <Alert variant="warning" className="border-0 shadow-sm">
          <div className="d-flex align-items-center gap-3">
            <span style={{ fontSize: '2rem' }}>📱</span>
            <div>
              <Alert.Heading className="h5">
                No Phone Number Found
              </Alert.Heading>
              <p className="mb-0">
                This user doesn't have a phone number associated with their
                account. Message intelligence requires a phone number for
                analysis.
              </p>
            </div>
          </div>
        </Alert>
      ) : (
        <>
          {/* Behavioral Profile Overview */}
          <BehavioralProfileOverview
            profile={profile}
            loading={loading}
            error={error}
            systemUser={phoneNumber}
          />

          {/* Permission Events Section */}
          <Card className="border-0 shadow-sm mt-4 bg-dark text-white">
            <Card.Header className="bg-dark border-0">
              <h5 className="fw-bold mb-2 text-white">
                Permission Events History
              </h5>
              <p className="text-muted mb-0">
                Detailed can-send decision history and event logs. Track when
                messages were allowed or blocked and understand the reasoning.
              </p>
            </Card.Header>
            <Card.Body className="bg-dark">
              <div className="mb-4">
                <PermissionEventsFilters
                  filters={filters}
                  onFiltersChange={handleFilterChange}
                  senderNumber={phoneNumber}
                  loading={isSummaryLoading}
                />
              </div>

              <PermissionEventsTable
                events={allEvents}
                loading={isEventsLoading}
                onLoadMore={handleLoadMore}
                hasMore={!!eventsData?.pagination.hasMore}
                onEventClick={handleEventClick}
              />
            </Card.Body>
          </Card>
        </>
      )}

      {/* Individual Behavioral Profile Modal */}
      <IndividualBehavioralProfileModal
        show={showIndividualModal}
        onHide={handleModalClose}
        profile={individualProfileData?.profile || null}
        loading={isIndividualProfileLoading}
        error={individualProfileError as Error | null}
        systemUser={selectedSystemUser}
        recipient={selectedRecipient}
      />
    </Container>
  )
}

export default UserPanopticonProfilePage
